import axios, { type AxiosInstance, AxiosError } from "axios";
import { getAccessToken, setAccessToken } from "../contexts/AuthContext";
import { ERROR_MESSAGES } from "./constants";

const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  if (import.meta.env.PROD) {
    return '';
  }
  
  return "http://localhost:3000";
};

const API_BASE_URL = getApiBaseUrl();

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {

    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      const timeoutError = new Error(ERROR_MESSAGES.TIMEOUT_ERROR);
      timeoutError.name = 'TimeoutError';
      return Promise.reject(timeoutError);
    }

    if (!error.response) {
      const networkError = new Error(ERROR_MESSAGES.NETWORK_ERROR);
      return Promise.reject(networkError);
    }

    const status = error.response.status;
    const data = error.response.data as { message?: string; code?: string } | undefined;

    // Tenta refresh silencioso em caso de token expirado
    if (status === 401 && data?.code === 'TOKEN_EXPIRED') {
      try {
        const refreshRes = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        const newToken: string = refreshRes.data.accessToken;
        setAccessToken(newToken);

        // Repete a requisição original com o novo token
        const originalRequest = error.config!;
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        // Refresh falhou — desloga o usuário
        setAccessToken(null);
        localStorage.removeItem("rememberMe");
        window.dispatchEvent(new CustomEvent("auth:logout", { detail: { reason: "Sessão expirada" } }));
        throw new Error(ERROR_MESSAGES.SESSION_EXPIRED);
      }
    }

    if (status === 401 && data?.code === 'ACCOUNT_INACTIVE') {
      setAccessToken(null);
      localStorage.removeItem("rememberMe");
      window.dispatchEvent(new CustomEvent("auth:logout", { detail: { reason: "account_inactive" } }));
      throw new Error(data?.message || 'Conta desativada');
    }

    if (status === 401) {
      setAccessToken(null);
      localStorage.removeItem("rememberMe");
      window.dispatchEvent(new CustomEvent("auth:logout", { detail: { reason: "Sessão expirada" } }));
      throw new Error(ERROR_MESSAGES.SESSION_EXPIRED);
    }

    switch (status) {
      case 403:
        throw new Error(ERROR_MESSAGES.PERMISSION_DENIED);

      case 404:
        throw new Error(data?.message || ERROR_MESSAGES.RESOURCE_NOT_FOUND);

      case 409:
        throw new Error(data?.message || ERROR_MESSAGES.DUPLICATE_RESOURCE);

      case 422:
        throw new Error(
          data?.message ||
            "Dados inválidos. Verifique os campos preenchidos e tente novamente."
        );

      case 429:
        throw new Error(ERROR_MESSAGES.RATE_LIMIT);

      case 500:
        throw new Error(ERROR_MESSAGES.SERVER_ERROR);

      case 502:
      case 503:
      case 504:
        throw new Error(ERROR_MESSAGES.SERVICE_UNAVAILABLE);

      default:
        throw new Error(
          data?.message || ERROR_MESSAGES.UNKNOWN_ERROR
        );
    }
  }
);

export default api;
